import { useQuery, useInfiniteQuery } from "@tanstack/react-query"
import {
  getVideoLogs,
  getVideoLogById,
  getVideoLogRow,
  getVideoScanSummary,
  getVideoScanAnalytics,
  getVideoScanFilterOptions,
  getVideoScanDashboard,
  getVideoAnalytics,
  getVideoErrorStats,
  getVideoStatsCases,
} from "../api/videoLogs"

export const useVideoLogs = ({ limit = 20, sortBy, filters }) =>
  useInfiniteQuery({
    queryKey: ["videoLogs", "infinite", sortBy, filters],
    queryFn: ({ pageParam = 0 }) =>
      getVideoLogs({
        offset: pageParam,
        limit,
        sortBy,
        ...filters,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce(
        (sum, page) => sum + page.data.data.length,
        0
      )
      return lastPage.data.data.length === limit ? loadedItems : undefined
    },
    initialPageParam: 0,
    retry: 0,
    staleTime: 0,
    refetchInterval: 1 * 60 * 1000,
    select: (data) => {
      const lastPage = data?.pages?.[data?.pages?.length - 1]?.data
      const flattenedData = data?.pages?.flatMap((page) => page.data.data) || []
      return {
        data: flattenedData,
        total: lastPage?.total || 0,
        page: lastPage?.page || 1,
        limit: lastPage?.limit || limit,
        totalPages: lastPage?.totalPages || 1,
      }
    },
  })

/*
  staleTime matters here: the Video Logs list selects as focus moves, so arrowing
  through records fires one of these per keystroke. Caching for a minute makes
  scrubbing back and forth free, and the list itself refetches on that same
  interval, so nothing goes stale for longer than the list does.
*/
export const useVideoLogById = (reportId) =>
  useQuery({
    queryKey: ["videoLogById", reportId],
    queryFn: () => getVideoLogById(reportId),
    retry: 0,
    enabled: !!reportId,
    staleTime: 60 * 1000,
    select: (data) => data?.data || null,
  })

/*
  The row behind the detail pane's header, video list, and scan card. Fetched by
  id on its own — not derived from the paginated list — so opening a record
  costs one request regardless of how far down a large, filtered list it sits,
  or whether it is even in the sidebar's current filters at all. A record that
  does not exist (or that this user cannot access) 404s, which react-query
  surfaces as isError rather than a value — the caller treats that as "not
  found" rather than retrying, hence retry: 0.
*/
export const useVideoLogRow = (reportId) =>
  useQuery({
    queryKey: ["videoLogRow", reportId],
    queryFn: () => getVideoLogRow(reportId),
    retry: 0,
    enabled: !!reportId,
    staleTime: 60 * 1000,
    select: (data) => data?.data || null,
  })

export const useVideoScanSummary = (uuid) =>
  useQuery({
    queryKey: ["videoScanSummary", uuid],
    queryFn: () => getVideoScanSummary(uuid),
    retry: 0,
    enabled: !!uuid,
    staleTime: 60 * 1000,
    select: (data) => data?.data || null,
  })

export const useVideoScanAnalytics = ({ limit = 20, sortBy, filters }) =>
  useInfiniteQuery({
    queryKey: ["videoScanAnalytics", "infinite", sortBy, filters],
    queryFn: ({ pageParam = 0 }) =>
      getVideoScanAnalytics({
        offset: pageParam,
        limit,
        sortBy,
        ...filters,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce(
        (sum, page) => sum + page.data.data.length,
        0
      )
      return lastPage.data.data.length === limit ? loadedItems : undefined
    },
    initialPageParam: 0,
    retry: 0,
    staleTime: 0,
    select: (data) => {
      const lastPage = data?.pages?.[data?.pages?.length - 1]?.data
      const flattenedData = data?.pages?.flatMap((page) => page.data.data) || []
      return {
        data: flattenedData,
        // Totals describe the whole filtered set, not the loaded pages.
        totals: lastPage?.totals || {},
        total: lastPage?.total || 0,
        limit: lastPage?.limit || limit,
        totalPages: lastPage?.totalPages || 1,
      }
    },
  })

export const useVideoScanFilterOptions = () =>
  useQuery({
    queryKey: ["videoScanFilterOptions"],
    queryFn: getVideoScanFilterOptions,
    retry: 0,
    staleTime: 5 * 60 * 1000,
    select: (data) => data?.data || { facilities: [], patientIssuers: [] },
  })

export const useVideoScanDashboard = (filters) =>
  useQuery({
    queryKey: ["videoScanDashboard", filters],
    queryFn: () => getVideoScanDashboard(filters),
    retry: 0,
    staleTime: 0,
    select: (data) =>
      data?.data || {
        totals: {},
        reportsIssued: 0,
        facilityBreakdown: [],
        bodyPartBreakdown: [],
        dailyVolume: [],
        centerScanRates: [],
        specialtyBreakdown: [],
        orderingPhysicianStudyBreakdown: [],
        orderingPhysicianScanBreakdown: [],
        experimentVariantBreakdown: [],
      },
  })

export const useVideoAnalytics = (filters) =>
  useQuery({
    queryKey: ["videoAnalytics", filters],
    queryFn: () => getVideoAnalytics(filters),
    retry: 0,
    staleTime: 0,
    select: (data) => data?.data?.videos || [],
  })

/*
  QR Analytics is a date-range report, not a live monitor, so these two don't
  poll: they refetch when the filters change, and otherwise stay fresh for five
  minutes (which also stops a tab refocus from re-running the slow queries).
*/
const REPORT_STALE_TIME = 5 * 60 * 1000

export const useVideoErrorSummary = (filters) =>
  useQuery({
    queryKey: ["videoErrorSummary", filters],
    queryFn: () => getVideoErrorStats({ ...filters, summaryOnly: true }),
    retry: 0,
    staleTime: REPORT_STALE_TIME,
    select: (data) => data?.data?.summary,
  })

export const useVideoStatsCases = ({ limit = 20, filters }) =>
  useInfiniteQuery({
    queryKey: ["videoStatsCases", "infinite", filters],
    queryFn: ({ pageParam = 0 }) =>
      getVideoStatsCases({
        offset: pageParam,
        limit,
        ...filters,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce(
        (sum, page) => sum + page.data.groups.length,
        0
      )
      return lastPage.data.groups.length === limit ? loadedItems : undefined
    },
    initialPageParam: 0,
    retry: 0,
    staleTime: REPORT_STALE_TIME,
    select: (data) => {
      const firstPage = data?.pages?.[0]?.data
      const lastPage = data?.pages?.[data?.pages?.length - 1]?.data
      const groups = data?.pages?.flatMap((page) => page.data.groups) || []
      return {
        summary: firstPage?.summary || { totalRecords: 0 },
        categories: firstPage?.categories || [],
        category: firstPage?.category || null,
        groups,
        total: lastPage?.total || 0,
        totalPages: lastPage?.totalPages || 1,
      }
    },
  })
